import clsx from "clsx";
import React, { ReactElement } from "react";
import { BaseCardSanityImageProps } from "../BaseCard/BaseCard";
import { PromoBadgeContent, PromoBadges } from "../PromoBadges/PromoBadges";
import styles from "./HorizontalSuiteCardBase.module.scss";
import { isSanityImagePresent } from "../SanityImage";
import { ResponsiveSanityImage } from "../ResponsiveSanityImage/ResponsiveSanityImage";
import { Price } from "../types/price";
import { PriceItem } from "../Prices/PriceItem/PriceItem";

type SuiteCardBaseVariant = "default" | "selected" | "blurred";

export type HorizontalSuiteCardBaseProps = {
  title?: string;
  subtitle?: ReactElement;
  image?: BaseCardSanityImageProps["image"];
  trackingName: string;
  className?: string;
  features: (string | ReactElement)[];
  actionButton?: ReactElement;
  detailsButton: ReactElement;
  variant?: SuiteCardBaseVariant;
  imageFooterComponent?: ReactElement;
  activePromos?: PromoBadgeContent[];
  prices: Price[];
  pricesDisclaimer?: string;
  featuresSectionTitle: string;
};

export const HorizontalSuiteCardBase: React.FC<
  HorizontalSuiteCardBaseProps
> = ({
  image,
  title,
  subtitle,
  trackingName,
  className,
  features,
  actionButton,
  detailsButton,
  imageFooterComponent,
  variant = "default",
  prices,
  pricesDisclaimer,
  featuresSectionTitle,
  activePromos = []
}) => {
  return (
    <article
      className={clsx(styles.card, className, {
        [styles.cardSelected]: variant == "selected",
        [styles.cardBlurred]: variant == "blurred"
      })}
      data-content-name={trackingName}
    >
      {isSanityImagePresent(image) && (
        <div className={styles.image}>
          <ResponsiveSanityImage
            image={image}
            defaultWidth={400}
            defaultHeight={300}
            draggable={false}
            loading="lazy"
            className={styles.imageInner}
            alt={title}
          />
          <div className={styles.imageFooter}>{imageFooterComponent}</div>
        </div>
      )}

      <div className={styles.title}>
        {title}
        {subtitle}
        {activePromos.length > 0 ? (
          <PromoBadges
            promos={activePromos}
            className={styles.promoBox}
            variant="small"
          />
        ) : null}
      </div>

      <div className={styles.features}>
        <div className={styles.columnTitle}>{featuresSectionTitle}</div>
        <ul>
          {features.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      </div>
      <div className={styles.pricesColumn}>
        {pricesDisclaimer && (
          <div className={styles.columnTitle}>{pricesDisclaimer}</div>
        )}
        <div className={styles.prices}>
          {prices.map((p, index) => {
            return (
              <React.Fragment key={p.fareLabel}>
                {index == 0 ? null : <hr />}
                <div className={styles.priceItemWrapper}>
                  <div className={styles.fareLabel}>{p.fareLabel}</div>
                  <PriceItem
                    className={styles.priceItem}
                    priceClassName={styles.priceItemInner}
                    layout="column"
                    showFareLabel={false}
                    chipBackgroundColor="white"
                    price={p}
                    variant="normal"
                    priceType={index == 0 ? "primary" : "secondary"}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div className={styles.footer}>
        {detailsButton} {actionButton}
      </div>
    </article>
  );
};
